'use client'

import { useState } from 'react'
import {
  ArrowRight,
  Calculator,
  Education as GraduationCap,
  Help as HelpCircle,
  Chat as MessageSquare,
  Group as Users,
  CurrencyDollar as DollarSign,
  Enterprise as Building2,
  ChevronDown,
  ChevronUp,
  Document as FileText,
  PlayOutline as PlayCircle,
  Calendar,
  Email as Mail,
  Phone,
  Location as MapPin,
  AiGenerate as Sparkles,
} from '@carbon/icons-react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'

// ── Products ────────────────────────────────────────────────────────────────
const products = [
  {
    id: 'udc',
    title: 'UDC',
    fullTitle: 'Unified Digital Campus',
    description:
      'Campus data integration, student portal, and academic operations.',
    icon: Building2,
    href: '/products/udc',
  },
  {
    id: 'hrms',
    title: 'HRMS',
    fullTitle: 'HR Management System',
    description:
      'Employee lifecycle, recruitment, attendance, and performance.',
    icon: Users,
    href: '/products/hrms',
  },
  {
    id: 'payroll',
    title: 'Payroll',
    fullTitle: 'Payroll Management',
    description:
      'Salary processing, tax compliance, and automated calculations.',
    icon: DollarSign,
    href: '/products/payroll',
  },
  {
    id: 'accounts',
    title: 'Accounts',
    fullTitle: 'Accounting System',
    description: 'Financial operations, reporting, and real-time insights.',
    icon: Calculator,
    href: '/products/accounts',
  },
  {
    id: 'lms',
    title: 'LMS',
    fullTitle: 'Learning Management System',
    description: 'Course management, training portals, and skill development.',
    icon: GraduationCap,
    href: '/products/lms',
  },
]

// ── FAQs ────────────────────────────────────────────────────────────────────
const faqs = [
  {
    q: 'How do I get started with a new product after purchase?',
    a: 'After your purchase is confirmed, our onboarding team will reach out within 2 business days to schedule a kickoff session. You will receive access credentials, documentation, and a dedicated point of contact.',
  },
  {
    q: 'Can multiple products be integrated together?',
    a: 'Yes. The IntelQI product suite UDC, HRMS, Payroll, Accounts, and LMS is designed to work seamlessly together. Our team handles the integration setup during onboarding.',
  },
  {
    q: 'What happens if I face a critical issue during working hours?',
    a: 'You can raise a support request using the form below. For critical issues, mark the priority as "Critical" and our team will respond on priority. You can also reach out to your dedicated account manager directly.',
  },
  {
    q: 'Is training provided for our staff when we adopt a new product?',
    a: 'Yes. We provide structured onboarding training for your team as part of the implementation process. Additional training sessions can also be arranged on request.',
  },
  {
    q: 'How do I request a new feature or report a bug?',
    a: 'Use the support request form and select "Feature Request" or "Bug Report" as the request type. Our product team reviews all submissions and you will be notified of the status.',
  },
  {
    q: 'Where can I find the latest product updates and improvements?',
    a: 'All product updates, new features, and improvements are published on our Product Updates page. You can visit it anytime to stay current with the latest changes across all IntelQI products.',
  },
]

// ── Quick Links ─────────────────────────────────────────────────────────────
const quickLinks = [
  {
    title: 'Product Updates',
    description: 'Latest features and improvements',
    icon: Sparkles,
    href: '/product-updates',
  },
  {
    title: 'Product Brochure',
    description: 'Download our latest product brochure',
    icon: PlayCircle,
    href: '/product-brochure',
  },
  {
    title: 'Blogs',
    description: 'Latest updates and best practices',
    icon: FileText,
    href: '/blogs',
  },
]

// ── Contact Info ────────────────────────────────────────────────────────────
const contactInfo = {
  email: 'hq@euclideum.com',
  phone: '+91 9930202329',
  address:
    'Euclideum Solutions Private Limited, 203A, Shiv Heights, Ambedkar Chowk, Thane West – 400601',
  supportHours: 'Monday–Friday, 9:00 AM – 6:00 PM IST',
}

// ── FAQ Item ────────────────────────────────────────────────────────────────
function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="cursor-pointer py-5" onClick={() => setOpen(!open)}>
      <div className="flex items-center justify-between gap-4">
        <p className="text-base font-medium text-foreground">{q}</p>
        {open ? (
          <ChevronUp className="size-5 text-emerald-600 dark:text-primary-active shrink-0" />
        ) : (
          <ChevronDown className="size-5 text-muted-foreground shrink-0" />
        )}
      </div>
      {open && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {a}
        </p>
      )}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function SupportPage() {
  const [loading, setLoading] = useState(false)
  const key = '1076a0f3-044b-42f7-b342-8420b6ecee85'
  const [errorMsg, setErrorMsg] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    institution: '',
    product: '',
    requestType: '',
    description: '',
  })
  const [submitted, setSubmitted] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (loading) return

    setLoading(true)
    setErrorMsg('')
    setSuccessMsg('')

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          access_key: key,

          name: formData.name?.trim(),
          institution: formData.institution?.trim(),
          product: formData.product?.trim(),
          request_type: formData.requestType?.trim(),
          description: formData.description?.trim(),

          subject: 'New Support Request - Euclideum',
          from_name: 'Euclideum Support',

          botcheck: '',
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Submission failed')
      }

      setSuccessMsg('Your request has been submitted successfully!')
      setSubmitted(true)
      // reset form
      setFormData({
        name: '',
        institution: '',
        product: '',
        requestType: '',
        description: '',
      })
    } catch (error: any) {
      setErrorMsg('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }
  return (
    <main className="relative bg-background">
      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-background text-foreground">
        <div className="container relative z-10 py-16 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left */}
            <div className="flex flex-col justify-center space-y-8">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-black uppercase tracking-[0.24em]">
                    Euclideum Support Centre
                  </span>
                </div>
                <h1 className="font-heading text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl">
                  <ChromaText>How can we help you?</ChromaText>
                </h1>
                <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base md:text-[17px]">
                  Get help with any of our products, raise a support request,
                  browse product updates, or book a session with our team.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="font-medium" asChild>
                  <Link href="#raise-request">Raise a Request</Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link href="#faq">
                    Browse FAQs
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right support highlights only */}
            <div className="relative hidden lg:flex items-center justify-end">
              <div className="grid grid-cols-1 gap-4 w-full max-w-sm">
                {[
                  {
                    icon: MessageSquare,
                    title: 'Raise a Support Request',
                    desc: 'Submit your issue and our team will respond promptly.',
                    href: '#raise-request',
                  },
                  {
                    icon: Sparkles,
                    title: 'Product Updates',
                    desc: 'Stay current with the latest features and improvements.',
                    href: '/product-updates',
                  },
                  {
                    icon: HelpCircle,
                    title: 'Frequently Asked Questions',
                    desc: 'Find quick answers to the most common questions.',
                    href: '#faq',
                  },
                ].map(({ icon: Icon, title, desc, href }) => (
                  <Link
                    key={title}
                    href={href}
                    className="flex items-start gap-4 rounded-xl border border-border/70 bg-background p-4"
                  >
                    <Icon className="mt-0.5 size-6 shrink-0 text-emerald-600 dark:text-primary-active" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {title}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {desc}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile-only highlights */}
          <div className="mt-10 lg:hidden grid gap-4">
            {[
              {
                icon: MessageSquare,
                title: 'Raise a Support Request',
                href: '#raise-request',
              },
              {
                icon: Sparkles,
                title: 'Product Updates',
                href: '/product-updates',
              },
              {
                icon: HelpCircle,
                title: 'Frequently Asked Questions',
                href: '#faq',
              },
            ].map(({ icon: Icon, title, href }) => (
              <Link
                key={title}
                href={href}
                className="flex items-center gap-4 rounded-xl border border-border/70 bg-background p-4"
              >
                <Icon className="size-6 text-emerald-600 dark:text-primary-active" />
                <span className="font-medium">{title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      {/* ── Raise a Request ── */}
      <section id="raise-request" className="bg-background py-8 lg:py-16">
        <div className="container">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
            <div className="space-y-8 lg:sticky lg:top-24">
              <h2 className="font-heading text-3xl font-normal tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                <ChromaText>Raise a support request</ChromaText>
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground">
                Fill in the form and our support team will get back to you. For
                critical issues, select the appropriate priority and we will
                treat it accordingly.
              </p>

              <div className="space-y-4 pt-4 border-t border-border">
                <p className="text-sm font-medium text-foreground">
                  Direct Contact
                </p>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-3">
                    <Mail className="size-5 text-emerald-600 dark:text-primary-active" />
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="hover:text-emerald-600 dark:hover:text-primary-active"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="size-5 text-emerald-600 dark:text-primary-active" />
                    <a
                      href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                      className="hover:text-emerald-600 dark:hover:text-primary-active"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="size-5 text-emerald-600 dark:text-primary-active mt-0.5" />
                    <span>{contactInfo.address}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              {submitted ? (
                <div className="rounded-2xl border border-emerald-600/30 dark:border-primary-active/30 bg-emerald-600/5 dark:bg-primary-active/5 p-10 text-center space-y-4">
                  <div className="flex size-16 items-center justify-center rounded-full bg-emerald-600/20 dark:bg-primary-active/20 mx-auto">
                    <MessageSquare className="size-8 text-emerald-600 dark:text-primary-active" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-foreground">
                    Request Submitted!
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Our team has received your request and will get back to you
                    within 1 business day.
                  </p>

                  <Button
                    variant="outline"
                    onClick={() => {
                      setSubmitted(false)
                      setErrorMsg('')
                    }}
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : errorMsg ? (
                <div className="rounded-2xl border border-red-300 bg-red-50 p-10 text-center space-y-4">
                  <div className="flex size-16 items-center justify-center rounded-full bg-red-100 mx-auto">
                    <MessageSquare className="size-8 text-red-500" />
                  </div>

                  <h3 className="font-heading text-2xl font-semibold text-foreground">
                    Submission Failed
                  </h3>

                  <p className="text-sm text-muted-foreground">
                    {'There was a server error. Please try again later.'}
                  </p>

                  <Button variant="outline" onClick={() => setErrorMsg('')}>
                    Try Again
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-muted-foreground">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/80 focus:border-emerald-600 dark:focus:border-primary-active focus:outline-none focus:ring-2 focus:ring-emerald-600/20 dark:focus:ring-primary-active/20"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-muted-foreground">
                      Institution / Organization{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="Your institution or organization name"
                      value={formData.institution}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          institution: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/80 focus:border-emerald-600 dark:focus:border-primary-active focus:outline-none focus:ring-2 focus:ring-emerald-600/20 dark:focus:ring-primary-active/20"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-muted-foreground">
                        Product <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.product}
                        onChange={(e) =>
                          setFormData({ ...formData, product: e.target.value })
                        }
                        className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-foreground focus:border-emerald-600 dark:focus:border-primary-active focus:outline-none focus:ring-2 focus:ring-emerald-600/20 dark:focus:ring-primary-active/20"
                      >
                        <option value="">Select product</option>
                        <option>UDC</option>
                        <option>HRMS</option>
                        <option>Payroll</option>
                        <option>Accounts</option>
                        <option>LMS</option>
                        <option>General / Other</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-muted-foreground">
                        Request Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={formData.requestType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            requestType: e.target.value,
                          })
                        }
                        className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-foreground focus:border-emerald-600 dark:focus:border-primary-active focus:outline-none focus:ring-2 focus:ring-emerald-600/20 dark:focus:ring-primary-active/20"
                      >
                        <option value="">Select type</option>
                        <option>Bug Report</option>
                        <option>Feature Request</option>
                        <option>General Query</option>
                        <option>Onboarding Help</option>
                        <option>Critical Issue</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-muted-foreground">
                      Describe your issue{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      required
                      rows={5}
                      placeholder="Please describe your issue or request in detail..."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className="w-full rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/80 focus:border-emerald-600 dark:focus:border-primary-active focus:outline-none focus:ring-2 focus:ring-emerald-600/20 dark:focus:ring-primary-active/20 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="w-full font-medium disabled:opacity-60"
                  >
                    {loading ? 'Submitting...' : 'Submit Request'}
                    {!loading && <ArrowRight className="ml-2 size-4" />}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section
        id="faq"
        className="relative bg-background py-8 lg:py-16 text-foreground"
      >
        <div className="container">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="space-y-6 lg:sticky lg:top-24">
              <p className="text-[11px] font-black uppercase tracking-[0.24em] text-foreground">
                FAQ
              </p>
              <h2 className="font-heading text-3xl font-normal tracking-tight sm:text-4xl lg:text-5xl">
                <ChromaText>Frequently asked questions</ChromaText>
              </h2>
              <p className="text-base text-muted-foreground">
                Can't find what you're looking for? Raise a support request and
                our team will help you out.
              </p>
              <Button variant="outline" asChild>
                <Link href="#raise-request">
                  Raise a Request
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>

            <div className="divide-y divide-border/70">
              {faqs.map((faq) => (
                <FAQItem key={faq.q} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick Links ── */}
      <section className="bg-background py-8 lg:py-12">
        <div className="container">
          <div className="mb-8 space-y-2">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-foreground">
              Resources
            </p>
            <h2 className="font-heading text-3xl font-normal tracking-tight text-foreground lg:text-4xl">
              <ChromaText>Helpful resources</ChromaText>
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {quickLinks.map(({ title, description, icon: Icon, href }) => (
              <Link
                key={title}
                href={href}
                className="group flex items-start gap-4 rounded-xl border border-border/70 bg-background p-6"
              >
                <Icon className="mt-0.5 size-6 shrink-0 text-foreground/70" />
                <div className="flex-1">
                  <p className="text-base font-semibold text-foreground mb-1">
                    {title}
                  </p>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
                <ArrowRight className="mt-1 size-4 shrink-0 text-muted-foreground/80 transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="bg-background pb-12 lg:pb-16">
        <div className="container">
          <div className="space-y-6 rounded-xl bg-muted px-8 py-12 text-center text-foreground dark:bg-muted/60">
            <Calendar className="mx-auto size-9 text-emerald-600 dark:text-primary-active" />
            <h2 className="font-heading text-3xl font-normal lg:text-4xl">
              Need a deeper conversation?
            </h2>
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              Book a session with our team for onboarding help, product
              walkthroughs, or strategic guidance on implementing our solutions
              at your institution.
            </p>
            <Button size="lg" className="font-medium" asChild>
              <Link href="/schedule-meeting">
                Book an Appointment
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>

            <div className="pt-6 border-t border-border dark:border-white/10">
              <p className="text-sm text-foreground/70 pb-3">
                Or reach us directly:
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-6 lg:gap-10 text-xs lg:text-sm text-foreground/90">
                {' '}
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-3 hover:text-emerald-600 dark:hover:text-primary-active transition-colors"
                >
                  <Mail className="size-5 text-foreground" />
                  {contactInfo.email}
                </a>
                <a
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 hover:text-emerald-600 dark:hover:text-primary-active transition-colors"
                >
                  <Phone className="size-5 text-foreground" />
                  {contactInfo.phone}
                </a>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:text-emerald-600 dark:hover:text-primary-active transition-colors"
                >
                  <MapPin className="size-5 text-foreground" />
                  {contactInfo.address}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
